import { GridFilterItem } from '@mui/x-data-grid-pro';

import { PART_FILTER_FIELD_DEFINITIONS } from './partFilterFields';
import { FilterState, OptionGroup } from '../../../components/molecules/FilterBar';
import { ColumnDef, applyColumnOrder } from '../../../components/organisms/ServiceTable';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { partListColumnSelectionItems } from '../../../constants/part';
import { advancedFiltersFromModel } from '../../../utils/advancedFilterFromModel';
import { buildAdvancedFilterItems } from '../../../utils/filterModel';

export interface LabelValue {
  label: string;
  value: string;
}

/** Splits a serial-search value on common paste delimiters (commas,
 *  semicolons, and any whitespace — including spaces, tabs, and newlines).
 *  Returns the de-duplicated list of trimmed tokens when the input resolves
 *  to two or more distinct tokens, or `null` otherwise — including the edge
 *  cases where the raw input contains a delimiter but only collapses to a
 *  single real token (e.g. "AB-001,", "AB-001\n", "AB-001,AB-001"). Callers
 *  fall back to the existing substring `serial_search` behaviour in the null
 *  case so quick partial-serial typing keeps working. Serial numbers in this
 *  system never contain spaces, so splitting on whitespace is safe. */
const MULTI_SERIAL_DELIMITER = /[,;\s]+/;
export const parseMultiSerial = (input: string): string[] | null => {
  if (!input || !MULTI_SERIAL_DELIMITER.test(input)) return null;
  const tokens = Array.from(
    new Set(
      input
        .split(MULTI_SERIAL_DELIMITER)
        .map((t) => t.trim())
        .filter(Boolean)
    )
  );
  if (tokens.length <= 1) return null;
  return tokens;
};

/** Parse the inline "Part Type" box into part-type IDs vs name fragments. A token
 *  is an ID when it is bare digits or the `AM-PTID-<id>` code (prefix stripped,
 *  case-insensitive); anything else is treated as a name fragment. Tokens are split
 *  on commas / semicolons / newlines / tabs (NOT spaces — part type names contain
 *  spaces, e.g. "Ingenico MOVE 5000"), so a comma- or newline-separated list of IDs
 *  is recognised while a typed name phrase stays intact. Lets that one box search by
 *  part type name (as before), by exact part type ID, or by a pasted list of either. */
const PART_TYPE_DELIMITER = /[,;\n\r\t]+/;
const AM_PTID_PREFIX = /^am-ptid-/i;
export const parsePartTypeQuery = (input: string): { ids: string[]; names: string[] } => {
  const tokens = Array.from(
    new Set(
      input
        .split(PART_TYPE_DELIMITER)
        .map((t) => t.trim())
        .filter(Boolean)
    )
  );
  const ids: string[] = [];
  const names: string[] = [];
  for (const token of tokens) {
    const stripped = token.replace(AM_PTID_PREFIX, '');
    if (/^\d+$/.test(stripped)) ids.push(stripped);
    else names.push(token);
  }
  return { ids, names };
};

/** Default filter state for PartList and usePartReports. The Serialised
 *  dropdown is pre-populated so the chip is visible to the user and
 *  preserved when they add other filters, rather than being silently
 *  applied behind the scenes. */
export const EMPTY_FILTERS: FilterState = {
  statuses: [],
  jobTypes: [],
  contracts: ['Serialised'],
  contractNames: [],
  extraText: '',
  jobId: '',
  multiSearch: '',
  advancedFilters: [],
};

/** Default visible columns when the user has no saved report configured. */
const DEFAULT_VISIBLE_FIELDS = [
  'serial1',
  'serial2',
  'conditionName',
  'isActive',
  'partTypeName',
  'partTypeNumber',
  'quantityInStock',
  'location',
];

const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  assetId: 80,
  quantityInStock: 70,
};

const DEFAULT_COLUMN_MIN_WIDTHS: Record<string, number> = {
  location: 260,
  partTypeName: 180,
  // Prevents character-by-character wrap on hyphenated part numbers
  // (e.g. "A920-3AW-RD5-21EP") when the table is squeezed at laptop widths.
  partTypeNumber: 120,
};

const DEFAULT_COLUMN_MAX_WIDTHS: Record<string, number> = {
  notes: 200,
  exceptionResultNote: 240,
};

const DEFAULT_COLUMN_HEADER_LABELS: Record<string, string> = {
  assetId: 'Asset',
  quantityInStock: 'Qty',
  partTypeNumber: 'Part No.',
};

/** Implicit baseline applied when no quick filters are set. The Serialised
 *  filter lives in EMPTY_FILTERS so it shows as a visible chip; isActive
 *  stays implicit because the FilterBar has no slot to display it. */
export const DEFAULT_FILTER_ITEMS: GridFilterItem[] = [
  { field: 'isActive', operator: 'is', value: 'true' },
];

/** Build column-picker state with `value` aligned to DEFAULT_VISIBLE_FIELDS so
 *  the picker's ticked set matches what the table actually renders on first
 *  load. The `value` flags in the raw `partListColumnSelectionItems` constant
 *  are ignored — DEFAULT_VISIBLE_FIELDS is the single source of truth. */
export const buildInitialColumnSelection = (): ColumnSelectionItem[] =>
  partListColumnSelectionItems.map((group) => ({
    ...group,
    value: group.value.map((col) => ({
      ...col,
      value: DEFAULT_VISIBLE_FIELDS.includes(col.field),
    })),
  }));

export const buildColumnDefs = (): ColumnDef[] => {
  const defs: ColumnDef[] = [];
  for (const group of partListColumnSelectionItems) {
    for (const col of group.value) {
      defs.push({
        key: col.field,
        label: col.title,
        group: group.title,
        visible: DEFAULT_VISIBLE_FIELDS.includes(col.field),
        ...(DEFAULT_COLUMN_HEADER_LABELS[col.field] !== undefined
          ? { headerLabel: DEFAULT_COLUMN_HEADER_LABELS[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_WIDTHS[col.field] !== undefined
          ? { width: DEFAULT_COLUMN_WIDTHS[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_MIN_WIDTHS[col.field] !== undefined
          ? { minWidth: DEFAULT_COLUMN_MIN_WIDTHS[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_MAX_WIDTHS[col.field] !== undefined
          ? { maxWidth: DEFAULT_COLUMN_MAX_WIDTHS[col.field] }
          : {}),
      });
    }
  }
  return applyColumnOrder(
    defs,
    DEFAULT_VISIBLE_FIELDS.map((field) => ({ field }))
  );
};

/** Convert a GridFilterModel back into a FilterState for the FilterBar
 *  display. On the parts list the built-in quick-filter slots are:
 *    statuses   → contract (multi-select dropdown)
 *    jobTypes   → conditionName (multi-select dropdown — Part Condition)
 *    contracts  → serialised (multi-select dropdown)
 *    extraText  → partTypeName contains (text input — Part Type)
 *  `multiSearch` maps to serial search. `jobId` is unused (hidden).
 *  Everything else lands in `advancedFilters`. */
export const filterModelToFilterState = (model: { items: GridFilterItem[] }): FilterState => {
  const state: FilterState = {
    statuses: [],
    jobTypes: [],
    contracts: [],
    contractNames: [],
    extraText: '',
    jobId: '',
    multiSearch: '',
    advancedFilters: [],
  };

  // Predicates outside the quick-filter slots become Add Filter chips;
  // `advancedFiltersFromModel` owns that reconstruction.
  const rest: GridFilterItem[] = [];

  for (const item of model.items) {
    if (item.field === 'contract') {
      if (item.operator === 'isAnyOf' && Array.isArray(item.value)) {
        state.statuses.push(...item.value.map(String));
      } else if (item.operator === 'equals' && item.value) {
        state.statuses.push(String(item.value));
      }
    } else if (item.field === 'conditionName') {
      if (item.operator === 'isAnyOf' && Array.isArray(item.value)) {
        state.jobTypes.push(...item.value.map(String));
      } else if (item.operator === 'equals' && item.value) {
        state.jobTypes.push(String(item.value));
      }
    } else if (item.field === 'serialised' && item.value) {
      state.contracts.push(String(item.value));
    } else if (
      item.field === 'serial1' &&
      item.operator === 'isAnyOf' &&
      Array.isArray(item.value)
    ) {
      // Multi-paste serial search round-trip: surface the tokens back in the
      // multiSearch input rather than as an advanced-filter chip whose
      // `isAnyOf` operator the AddFilterModal text-field UI can't render.
      state.multiSearch = item.value.map(String).join(',');
    } else if (
      item.field === 'partTypeName' &&
      item.operator === 'contains' &&
      typeof item.value === 'string'
    ) {
      // Always consume `partTypeName contains <string>` items (even empty /
      // whitespace-only) so they don't leak into the catch-all and surface as
      // a junk advanced-filter chip. Trim on hydration so the UI matches what
      // buildFilterModel emits for the same input.
      const trimmed = item.value.trim();
      if (trimmed) state.extraText = trimmed;
    } else if (item.field === 'partTypeId' && item.operator === 'in' && item.value != null) {
      // Reverse of the "Part Type" box id-search: surface the ids back in the
      // extraText box (comma-joined) rather than letting the `partTypeId in`
      // item fall into the catch-all and render a stringified-array chip. Keeps
      // saved-report / URL round-trips working for id searches.
      const values = Array.isArray(item.value)
        ? item.value.map(String)
        : String(item.value).split(',');
      const ids = values.map((v) => v.trim()).filter(Boolean);
      if (ids.length > 0) state.extraText = ids.join(',');
    } else if (
      DEFAULT_FILTER_ITEMS.some(
        (d) => d.field === item.field && d.operator === item.operator && d.value === item.value
      )
    ) {
      // Implicit isActive default — skip display.
    } else {
      rest.push(item);
    }
  }

  state.advancedFilters = advancedFiltersFromModel(rest, PART_FILTER_FIELD_DEFINITIONS);

  return state;
};

/** Convert FilterState from the FilterBar into a GridFilterModel the BFF
 *  accepts for the parts list endpoint.
 *  Slot mapping:
 *    statuses    → contract
 *    jobTypes    → conditionName
 *    contracts   → serialised
 *    extraText   → partTypeName contains
 *    multiSearch → single token: serial_search substring; multi token (paste
 *                  with comma/semicolon/newline/tab): exact serial1 isAnyOf */
export const buildFilterModel = (filters: FilterState): { items: GridFilterItem[] } => {
  const items: GridFilterItem[] = [];

  if (filters.statuses.length === 1) {
    items.push({ field: 'contract', operator: 'equals', value: filters.statuses[0] });
  } else if (filters.statuses.length > 1) {
    items.push({ field: 'contract', operator: 'isAnyOf', value: filters.statuses });
  }

  if (filters.jobTypes.length === 1) {
    items.push({ field: 'conditionName', operator: 'equals', value: filters.jobTypes[0] });
  } else if (filters.jobTypes.length > 1) {
    items.push({ field: 'conditionName', operator: 'isAnyOf', value: filters.jobTypes });
  }

  // serialised has only 2 options; selecting both is "show all" — omit the filter
  if (filters.contracts.length === 1) {
    items.push({ field: 'serialised', operator: 'is', value: filters.contracts[0] });
  }

  // The "Part Type" box searches by name (as before), by exact part type ID, or by
  // a pasted list of either. All-ID input -> exact `partTypeId in [...]` (the `in`
  // operator maps to Op.in server-side, which is numeric-safe on the integer
  // partTypeId column, unlike the iLike-based `contains`/`isAnyOf`). Otherwise it
  // falls back to the historical name substring search over the token(s).
  const { ids: partTypeIds, names: partTypeNames } = parsePartTypeQuery(filters.extraText);
  const hasPartTypeQuery = partTypeIds.length > 0 || partTypeNames.length > 0;
  if (partTypeIds.length > 0 && partTypeNames.length === 0) {
    items.push({ field: 'partTypeId', operator: 'in', value: partTypeIds });
  } else {
    const nameTokens = [...partTypeNames, ...partTypeIds];
    if (nameTokens.length === 1) {
      items.push({ field: 'partTypeName', operator: 'contains', value: nameTokens[0] });
    } else if (nameTokens.length > 1) {
      items.push({ field: 'partTypeName', operator: 'isAnyOf', value: nameTokens });
    }
  }

  const hasQuickFilter =
    filters.statuses.length > 0 ||
    filters.jobTypes.length > 0 ||
    filters.contracts.length > 0 ||
    hasPartTypeQuery ||
    !!filters.multiSearch;
  if (!hasQuickFilter) {
    items.push(...DEFAULT_FILTER_ITEMS);
  }

  const multiSerialTokens = parseMultiSerial(filters.multiSearch);
  if (multiSerialTokens) {
    items.push({ field: 'serial1', operator: 'isAnyOf', value: multiSerialTokens });
  }

  for (const adv of filters.advancedFilters) {
    if (adv.operator === 'between') {
      const fromValue = adv.dateFrom ?? adv.value;
      const toValue = adv.dateTo ?? adv.value2 ?? '';
      if (adv.type === 'date') {
        if (fromValue) items.push({ field: adv.dataKey, operator: 'onOrAfter', value: fromValue });
        if (toValue) items.push({ field: adv.dataKey, operator: 'onOrBefore', value: toValue });
      } else {
        if (fromValue) items.push({ field: adv.dataKey, operator: '>=', value: fromValue });
        if (toValue) items.push({ field: adv.dataKey, operator: '<=', value: toValue });
      }
      continue;
    }
    // Parts talks to a lib-framework BFF (api-inventory-mgmt): collapses
    // repeated params, supports `isNotAnyOf` for multi-value `Is not`.
    items.push(...buildAdvancedFilterItems(adv, 'isNotAnyOf'));
  }

  return { items };
};

/** Fixed options for the Serialised dropdown. */
export const SERIALISED_OPTIONS: LabelValue[] = [
  { label: 'Serialised', value: 'Serialised' },
  { label: 'Consumable', value: 'Consumable' },
];

/** Condition dropdown options from the part-condition API. */
export const buildConditionOptions = (
  data: Array<{ label: string; value: string }> | undefined
): LabelValue[] => {
  if (!data) return [];
  return data.map((c) => ({ label: c.label, value: c.label }));
};

/** No condition grouping is meaningful for parts yet — return empty so the
 *  FilterBar falls back to a flat options list. */
export const buildConditionGroups = (): OptionGroup[] => [];

/** Contract dropdown options — reuses the same structure query Services
 *  does against the contract node type. */
export const buildContractOptions = (
  data: Array<{ name: string; isActive: boolean }> | undefined
): LabelValue[] => {
  if (!data) return [];
  return data
    .filter((c) => c.isActive)
    .map((c) => ({ label: c.name, value: c.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
};
